import * as React from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { LogoutIcon, SteamIcon, CopyIcon, AddIcon } from '@icons';
import { formattedNumber } from '@utils/formattedNumber.ts';
import gtmTracker from '@utils/gtmTracker';

import profileBgNomad from '@assets/images/nomad/profile.webp';
import profileBgDefault from '@assets/images/profile_default.png';

import { useMutationLogout } from '@hooks/api/auth/use-mutation-logout';
import { useQueryGetMe } from '@hooks/api/user/use-query-me';
import useEnglishTranslation from '@hooks/use-english-translation';
import useGetPointsIcon from '@hooks/use-get-points-icon.tsx';
import useIsInIframe from '@hooks/use-is-in-iframe';

import { Button } from '@components/button';

import { config } from '@/config.ts';

import * as Styled from './profile-info.styled';

const isBlitz = config.BRAND === 'blitz';
const isPmBrand = config.BRAND === 'pm';

const ProfileInfo = () => {
	const { t } = useTranslation(['profile', 'notifications', 'main']);
	const tEn = useEnglishTranslation(['profile', 'notifications', 'main']);
	const PointsIcon = useGetPointsIcon();
	const { data: userData } = useQueryGetMe();
	const { mutate: logout } = useMutationLogout();

	const isIframe = useIsInIframe();

	const brand = config.BRAND || 'default';
	const isNomad = brand === 'nomad';
	const isXpBrand = brand === 'ngu3' || brand === 'com2';

	const getPointsLabel = () => {
		if (isBlitz) return t('blitzCoins');
		if (isXpBrand) return t('xp_points');
		return t('pmPoints');
	};

	const handleTrackEvent = (label: string) => {
		gtmTracker.trackEvent('click', {
			event_category: 'User_profile',
			event_action: 'Click',
			event_label: label
		});
	};

	const copyToClipboard = (text: number | string, copyType: string) => {
		navigator.clipboard.writeText(text.toString());

		if (copyType === 'account') {
			handleTrackEvent(tEn('notifications:account_copied'));
			toast.success(t('notifications:account_copied'));
		} else if (copyType === 'steam') {
			handleTrackEvent(tEn('notifications:steam_copied'));
			toast.success(t('notifications:steam_copied'));
		}
	};

	const logoutHandler = () => {
		handleTrackEvent('Logout');
		logout();
	};

	if (!userData) return null;

	return (
		<Styled.Wrapper $backgroundImage={isNomad ? profileBgNomad : profileBgDefault}>
			<Styled.Header>
				<Styled.IdLabel onClick={() => copyToClipboard(userData.pm_id, 'account')}>
					Accound ID: <Styled.Id>{userData.pm_id}</Styled.Id>
					<Styled.Copy>
						<CopyIcon />
					</Styled.Copy>
				</Styled.IdLabel>
				{!isIframe && (
					<Styled.Logout>
						<Button onClick={logoutHandler} color='dark'>
							<LogoutIcon />
						</Button>
					</Styled.Logout>
				)}
			</Styled.Header>
			<Styled.Content>
				<Styled.Row>
					{PointsIcon}
					<Styled.Name>
						{getPointsLabel()}:<Styled.Value>{formattedNumber(userData.pm_points)}</Styled.Value>
					</Styled.Name>

					{isPmBrand && <Styled.SkinPlaceLink to={'/skinplace'}>
						<AddIcon />
					</Styled.SkinPlaceLink>}
				</Styled.Row>
				{!isBlitz && (
					<>
						<Styled.Row>
							<SteamIcon />
							<Styled.Name
								$isClickable={!!userData.steam_id}
								onClick={() => userData.steam_id && copyToClipboard(userData.steam_id, 'steam')}
							>
								Steam ID:
								<Styled.Value>{userData.steam_id || '-'}</Styled.Value>
								{userData.steam_id && (
									<Styled.Copy>
										<CopyIcon />
									</Styled.Copy>
								)}
							</Styled.Name>
						</Styled.Row>
						<Styled.Row>
							<Styled.Name>
								{t('tickets')}:<Styled.Value>0</Styled.Value>
							</Styled.Name>
						</Styled.Row>
					</>
				)}
			</Styled.Content>
		</Styled.Wrapper>
	);
};

export default ProfileInfo;
